import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { SupportCategory } from "../lib/api";

/**
 * The one in-product way to reach a person (Part 10.2).
 *
 * Every "contact support" affordance in the signed-in product goes through this
 * module, so there is exactly one destination to change and exactly one place where
 * the wording lives. Before Part 10 these were `mailto:` links: they opened an empty
 * mail client, told the customer nothing afterwards, and left OKMedia with no record
 * that anyone had asked for help.
 *
 * WHY THE EMAIL ADDRESS IS STILL EXPORTED FROM HERE
 *
 * Because two situations exist where an in-app link would be a promise the product
 * cannot keep:
 *
 *  - the ODIN API is unreachable, which is the same API the support desk is served
 *    by — a link to `/app/support` at that moment leads to a page that also cannot
 *    load; and
 *  - the person is not signed in, or cannot sign in, which is precisely when they
 *    most need to reach somebody.
 *
 * `SupportEmailFallback` exists for those cases and for no others. Every retained
 * address in the codebase, and the legal or operational reason it is retained, is
 * listed in `docs/support/SUPPORT_EMAIL_RETENTION.md`.
 */

/** The signed-in support desk. */
export const SUPPORT_PATH = "/app/support";

/**
 * Retained as a legal and operational fallback, never as the normal support route.
 * See `docs/support/SUPPORT_EMAIL_RETENTION.md` for every place it must remain.
 */
export const SUPPORT_FALLBACK_EMAIL = "post@okmedia.live";

/**
 * A link may name the topic it is about. This only pre-selects the topic in the
 * composer — arriving on a page is not the same as asking for help, so no case is
 * opened, nothing is sent, and the customer still writes and submits the message
 * themselves. The server revalidates the category regardless of what the browser
 * sends.
 */
export function supportHref(topic?: SupportCategory): string {
  return topic === undefined ? SUPPORT_PATH : `${SUPPORT_PATH}?topic=${topic}`;
}

/**
 * An inline link to the support desk. The label is passed in rather than derived,
 * because the useful label depends on where the customer is standing — "Billing
 * Support" next to an invoice, "Refund Support" under a refund they are chasing.
 */
export function SupportLink({
  topic,
  children,
  className,
}: {
  topic?: SupportCategory;
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <Link to={supportHref(topic)} className={className}>
      {children}
    </Link>
  );
}

/**
 * The email fallback, used ONLY where the support desk may itself be unreachable or
 * where the reader has no account. `reason` states, truthfully, why an address is
 * being offered here instead of the desk — a bare address with no explanation reads
 * as "email us" and quietly undoes Part 10.2.
 */
export function SupportEmailFallback({ reason }: { reason: string }): JSX.Element {
  return (
    <p className="support-fallback">
      {reason} <a href={`mailto:${SUPPORT_FALLBACK_EMAIL}`}>{SUPPORT_FALLBACK_EMAIL}</a>
    </p>
  );
}
